import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Layout,
  Card,
  Button,
  DataTable,
  Banner,
  Modal,
  Badge,
  Text,
  Box,
  InlineStack,
  BlockStack,
  DatePicker,
  Popover,
  Select,
} from '@shopify/polaris';
import { PolarisVizProvider, StackedAreaChart } from '@shopify/polaris-viz';
import '@shopify/polaris-viz/build/esm/styles.css';
import { authenticatedFetch } from './AuthComponent';

// ── Date helpers ──────────────────────────────────────────────────────────────

function toISODate(date) {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatDisplay(date) {
  if (!date) return 'Select date';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateTime(isoStr) {
  if (!isoStr) return '—';
  try {
    const d = new Date(isoStr + 'Z');
    return d.toLocaleString('en-US', {
      month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch {
    return isoStr;
  }
}

function formatDuration(seconds) {
  if (seconds == null) return '—';
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  if (m < 60) return `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

// ── DatePicker Popover (same pattern as SpiritNameKits.jsx) ───────────────────

function DatePickerPopover({ label, selected, onSelect }) {
  const [open, setOpen] = useState(false);
  const initial = selected || new Date();
  const [{ month, year }, setMonthYear] = useState({
    month: initial.getMonth(),
    year: initial.getFullYear(),
  });

  const handleChange = ({ start }) => {
    onSelect(start);
    setOpen(false);
  };

  const activator = (
    <Button onClick={() => setOpen(o => !o)} disclosure>
      <span style={{ minWidth: 80, display: 'inline-block', textAlign: 'left' }}>
        <span style={{ fontSize: '0.75rem', color: '#6d7175', display: 'block' }}>{label}</span>
        {formatDisplay(selected)}
      </span>
    </Button>
  );

  return (
    <Popover
      active={open}
      activator={activator}
      onClose={() => setOpen(false)}
      preferredAlignment="left"
    >
      <Box padding="400">
        <DatePicker
          month={month}
          year={year}
          onMonthChange={(m, y) => setMonthYear({ month: m, year: y })}
          selected={selected ? { start: selected, end: selected } : undefined}
          onChange={handleChange}
        />
      </Box>
    </Popover>
  );
}

// ── Status Badge ──────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const map = {
    success: { tone: 'success', label: 'Success' },
    failed:  { tone: 'critical', label: 'Failed' },
    running: { tone: 'attention', label: 'Running' },
    stale:   { tone: 'warning', label: 'Stale' },
  };
  const cfg = map[status] || { tone: undefined, label: status };
  return <Badge tone={cfg.tone}>{cfg.label}</Badge>;
}

// ── Gantt Chart ───────────────────────────────────────────────────────────────

const STATUS_COLORS = {
  success: '#008060',
  failed:  '#d82c0d',
  running: '#ffc453',
  stale:   '#8c9196',
};

function GanttChart({ runs, startDate, endDate, onJobClick }) {
  const [tooltip, setTooltip] = useState(null);
  const tooltipRef = useRef(null);

  if (!runs || runs.length === 0) {
    return (
      <Box padding="400">
        <Text tone="subdued">No runs to display in this time range.</Text>
      </Box>
    );
  }

  const rangeStart = new Date(startDate + 'Z').getTime();
  const todayStr = toISODate(new Date());
  const rangeEnd = endDate >= todayStr
    ? Date.now()
    : new Date(endDate + 'T23:59:59Z').getTime();
  const rangeSpan  = rangeEnd - rangeStart;
  const rangeDays  = rangeSpan / (1000 * 60 * 60 * 24);

  // Generate smart time axis ticks based on range
  const getTimeTicks = () => {
    const MAX_TICKS = 12;
    let tickMs, fmt;
    if (rangeDays <= 1) {
      tickMs = 4 * 60 * 60 * 1000;
      fmt = (d) => d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' });
    } else if (rangeDays <= 4) {
      tickMs = 12 * 60 * 60 * 1000;
      fmt = (d) => {
        const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
        const h = d.getUTCHours();
        const ampm = h < 12 ? 'AM' : 'PM';
        return dateStr + ' ' + (h % 12 || 12) + ampm;
      };
    } else {
      tickMs = 24 * 60 * 60 * 1000;
      fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
    }
    const allTicks = [];
    let t = Math.ceil(rangeStart / tickMs) * tickMs;
    while (t <= rangeEnd) {
      const pct = ((t - rangeStart) / rangeSpan) * 100;
      if (pct >= 0 && pct <= 100) {
        allTicks.push({ pct, label: fmt(new Date(t)) });
      }
      t += tickMs;
    }
    // Thin ticks so we never show more than MAX_TICKS labels
    if (allTicks.length <= MAX_TICKS) return allTicks;
    const step = Math.ceil(allTicks.length / MAX_TICKS);
    return allTicks.filter((_, i) => i % step === 0);
  };
  const timeTicks = getTimeTicks();

  // Group runs by job name
  const jobMap = {};
  runs.forEach(run => {
    if (!jobMap[run.job_name]) jobMap[run.job_name] = [];
    jobMap[run.job_name].push(run);
  });
  const jobNames = Object.keys(jobMap);

  const toPercent = (isoStr) => {
    if (!isoStr) return null;
    const t = new Date(isoStr + 'Z').getTime();
    return Math.max(0, Math.min(100, ((t - rangeStart) / rangeSpan) * 100));
  };

  const handleMouseEnter = (e, run) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({ run, x: rect.left + window.scrollX, y: rect.top + window.scrollY - 8 });
  };

  const handleMouseLeave = () => setTooltip(null);

  return (
    <div style={{ overflowX: 'auto', position: 'relative' }}>
      {/* Header: time axis ticks */}
      <div style={{ marginLeft: 200, marginBottom: 4, paddingRight: 8, position: 'relative', height: 20, overflow: 'visible' }}>
        {timeTicks.map((tick, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: `${tick.pct}%`,
              transform: tick.pct < 5 ? 'translateX(0)' : tick.pct > 95 ? 'translateX(-100%)' : 'translateX(-50%)',
              whiteSpace: 'nowrap',
            }}
          >
            <Text variant="bodySm" tone="subdued">{tick.label}</Text>
          </div>
        ))}
      </div>
      {/* Grid lines */}
      <div style={{ marginLeft: 200, marginBottom: 4, paddingRight: 8, position: 'relative', height: 1, background: '#e1e3e5' }}>
        {timeTicks.map((tick, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: `${tick.pct}%`,
              top: 0,
              bottom: 0,
              width: 1,
              background: '#e1e3e5',
            }}
          />
        ))}
      </div>

      {jobNames.map(jobName => (
        <div
          key={jobName}
          style={{ display: 'flex', alignItems: 'center', marginBottom: 6, minHeight: 28 }}
        >
          {/* Job label */}
          <div
            style={{
              width: 200,
              minWidth: 200,
              paddingRight: 12,
              cursor: 'pointer',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            }}
            onClick={() => onJobClick(jobName)}
            title={jobName}
          >
            <Text variant="bodySm" fontWeight="semibold">{jobName}</Text>
          </div>

          {/* Track */}
          <div
            style={{
              flex: 1,
              height: 20,
              background: '#f6f6f7',
              borderRadius: 4,
              position: 'relative',
              border: '1px solid #e1e3e5',
              minWidth: 200,
            }}
          >
            {jobMap[jobName].map(run => {
              const leftPct  = toPercent(run.started_at);
              const nowIso   = new Date().toISOString().slice(0, 19);
              const rightPct = run.ended_at ? toPercent(run.ended_at) : toPercent(nowIso);
              if (leftPct === null) return null;
              const widthPct = Math.max(0, (rightPct || 100) - leftPct);

              return (
                <div
                  key={run.run_id}
                  onMouseEnter={e => handleMouseEnter(e, run)}
                  onMouseLeave={handleMouseLeave}
                  onClick={() => onJobClick(jobName)}
                  style={{
                    position: 'absolute',
                    left: `${leftPct}%`,
                    width: widthPct > 0.04 ? `${widthPct}%` : '4px',
                    minWidth: 4,
                    top: 2,
                    bottom: 2,
                    borderRadius: 3,
                    backgroundColor: STATUS_COLORS[run.status] || '#8c9196',
                    cursor: 'pointer',
                    opacity: 0.85,
                    transition: 'opacity 0.15s',
                  }}
                  onMouseOver={e => { e.currentTarget.style.opacity = '1'; }}
                  onMouseOut={e => { e.currentTarget.style.opacity = '0.85'; }}
                />
              );
            })}
          </div>
        </div>
      ))}

      {/* Tooltip */}
      {tooltip && (
        <div
          ref={tooltipRef}
          style={{
            position: 'fixed',
            left: Math.min(tooltip.x, window.innerWidth - 280),
            top: tooltip.y - 130,
            zIndex: 9999,
            background: '#fff',
            border: '1px solid #e1e3e5',
            borderRadius: 8,
            padding: '10px 14px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
            pointerEvents: 'none',
            minWidth: 240,
            maxWidth: 280,
          }}
        >
          <BlockStack gap="100">
            <InlineStack gap="200" align="space-between">
              <Text variant="bodyMd" fontWeight="semibold">{tooltip.run.job_name}</Text>
              <StatusBadge status={tooltip.run.status} />
            </InlineStack>
            <Text variant="bodySm" tone="subdued">Started: {formatDateTime(tooltip.run.started_at)}</Text>
            <Text variant="bodySm" tone="subdued">Ended: {formatDateTime(tooltip.run.ended_at)}</Text>
            <Text variant="bodySm" tone="subdued">Duration: {formatDuration(tooltip.run.duration_seconds)}</Text>
            <Text variant="bodySm" tone="subdued">Exit code: {tooltip.run.exit_code ?? '—'}</Text>
          </BlockStack>
        </div>
      )}
    </div>
  );
}

// ── Run Detail Modal ──────────────────────────────────────────────────────────

function RunDetailModal({ run, onClose }) {
  const [fullRun, setFullRun] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!run) return;
    authenticatedFetch(`/api/cron/runs/${run.run_id}`)
      .then(r => r.json())
      .then(data => { setFullRun(data); setLoading(false); })
      .catch(() => { setFullRun(run); setLoading(false); });
  }, [run]);

  if (!run) return null;
  const r = fullRun || run;

  return (
    <Modal
      open={!!run}
      onClose={onClose}
      title={`Run Details — ${r.job_name}`}
      size="large"
    >
      <Modal.Section>
        {loading ? (
          <Text tone="subdued">Loading...</Text>
        ) : (
          <BlockStack gap="400">
            {/* Header info */}
            <InlineStack gap="300" wrap>
              <StatusBadge status={r.status} />
              {r.command && (
                <Text variant="bodySm" tone="subdued">
                  <code style={{ background: '#f6f6f7', padding: '2px 6px', borderRadius: 4 }}>
                    {r.command}
                  </code>
                </Text>
              )}
            </InlineStack>

            {/* Times */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px' }}>
              <BlockStack gap="100">
                <Text variant="bodySm" tone="subdued">Started</Text>
                <Text variant="bodyMd">{formatDateTime(r.started_at)}</Text>
              </BlockStack>
              <BlockStack gap="100">
                <Text variant="bodySm" tone="subdued">Ended</Text>
                <Text variant="bodyMd">{formatDateTime(r.ended_at)}</Text>
              </BlockStack>
              <BlockStack gap="100">
                <Text variant="bodySm" tone="subdued">Duration</Text>
                <Text variant="bodyMd">{formatDuration(r.duration_seconds)}</Text>
              </BlockStack>
              <BlockStack gap="100">
                <Text variant="bodySm" tone="subdued">Exit Code</Text>
                <Text variant="bodyMd">{r.exit_code ?? '—'}</Text>
              </BlockStack>
            </div>

            {/* CPU / Memory */}
            {(r.cpu_peak_percent != null || r.mem_peak_mb != null) && (
              <Card>
                <Box padding="300">
                  <BlockStack gap="200">
                    <Text variant="headingSm">Resource Usage</Text>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '8px 16px' }}>
                      <BlockStack gap="100">
                        <Text variant="bodySm" tone="subdued">CPU Peak</Text>
                        <Text variant="bodyMd">{r.cpu_peak_percent != null ? `${r.cpu_peak_percent.toFixed(1)}%` : '—'}</Text>
                      </BlockStack>
                      <BlockStack gap="100">
                        <Text variant="bodySm" tone="subdued">CPU Avg</Text>
                        <Text variant="bodyMd">{r.cpu_avg_percent != null ? `${r.cpu_avg_percent.toFixed(1)}%` : '—'}</Text>
                      </BlockStack>
                      <BlockStack gap="100">
                        <Text variant="bodySm" tone="subdued">Mem Peak</Text>
                        <Text variant="bodyMd">{r.mem_peak_mb != null ? `${r.mem_peak_mb.toFixed(1)} MB` : '—'}</Text>
                      </BlockStack>
                      <BlockStack gap="100">
                        <Text variant="bodySm" tone="subdued">Mem Avg</Text>
                        <Text variant="bodyMd">{r.mem_avg_mb != null ? `${r.mem_avg_mb.toFixed(1)} MB` : '—'}</Text>
                      </BlockStack>
                    </div>
                  </BlockStack>
                </Box>
              </Card>
            )}

            {/* Stdout */}
            <BlockStack gap="200">
              <Text variant="headingSm">Stdout</Text>
              <div
                style={{
                  background: '#f6f6f7',
                  border: '1px solid #e1e3e5',
                  borderRadius: 6,
                  padding: '10px 14px',
                  maxHeight: 200,
                  overflowY: 'auto',
                }}
              >
                <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                  {r.stdout || '(no output)'}
                </pre>
              </div>
            </BlockStack>

            {/* Stderr */}
            <BlockStack gap="200">
              <Text variant="headingSm">Stderr</Text>
              <div
                style={{
                  background: r.stderr ? '#fff4f4' : '#f6f6f7',
                  border: `1px solid ${r.stderr ? '#ffd2d2' : '#e1e3e5'}`,
                  borderRadius: 6,
                  padding: '10px 14px',
                  maxHeight: 200,
                  overflowY: 'auto',
                }}
              >
                <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all', color: r.stderr ? '#d82c0d' : '#6d7175' }}>
                  {r.stderr || '(no output)'}
                </pre>
              </div>
            </BlockStack>
          </BlockStack>
        )}
      </Modal.Section>
      <Modal.Section>
        <InlineStack align="end">
          <Button onClick={onClose}>Close</Button>
        </InlineStack>
      </Modal.Section>
    </Modal>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

function CronMonitorTab() {
  const today = new Date();
  const defaultStart = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 2);

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate]     = useState(today);
  const [jobFilter, setJobFilter] = useState('');

  const [stats, setStats]         = useState(null);
  const [runs, setRuns]           = useState([]);
  const [jobs, setJobs]           = useState([]);
  const [timeseries, setTimeseries] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);

  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [detailRun, setDetailRun] = useState(null);
  const [activePreset, setActivePreset] = useState(3);

  const applyPreset = (days) => {
    const end = new Date();
    const start = days === 1
      ? new Date(Date.now() - 24 * 60 * 60 * 1000)
      : new Date(end.getFullYear(), end.getMonth(), end.getDate() - (days - 1));
    setEndDate(end);
    setStartDate(start);
    setActivePreset(days);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    const start = startDate.toISOString().slice(0, 19);
    const end   = toISODate(endDate)   + 'T23:59:59';

    try {
      const [runsRes, jobsRes, statsRes] = await Promise.all([
        authenticatedFetch(`/api/cron/runs?start_date=${start}&end_date=${end}${jobFilter ? `&job_name=${encodeURIComponent(jobFilter)}` : ''}`),
        authenticatedFetch(`/api/cron/jobs`),
        authenticatedFetch(`/api/cron/stats?start_date=${start}&end_date=${end}${jobFilter ? `&job_name=${encodeURIComponent(jobFilter)}` : ""}`),
      ]);

      if (!runsRes.ok)  throw new Error(`Runs fetch failed: ${runsRes.status}`);
      if (!jobsRes.ok)  throw new Error(`Jobs fetch failed: ${jobsRes.status}`);
      if (!statsRes.ok) throw new Error(`Stats fetch failed: ${statsRes.status}`);

      const [runsData, jobsData, statsData] = await Promise.all([
        runsRes.json(), jobsRes.json(), statsRes.json(),
      ]);

      setRuns(runsData.runs || []);
      setJobs(jobsData.jobs || []);
      setStats(statsData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, jobFilter]);

  const fetchTimeseries = useCallback(async (jobName) => {
    const start = startDate.toISOString().slice(0, 19);
    const end   = toISODate(endDate)   + 'T23:59:59';
    try {
      const rangeDays = (endDate - startDate) / (1000 * 60 * 60 * 24) + 1;
      const bucketHours = rangeDays <= 1 ? 1 : rangeDays <= 4 ? 2 : rangeDays <= 7 ? 4 : 12;
      const res = await authenticatedFetch(
        `/api/cron/timeseries/${encodeURIComponent(jobName)}?start_date=${start}&end_date=${end}&bucket_hours=${bucketHours}`
      );
      if (!res.ok) return;
      const data = await res.json();
      setTimeseries(data);
      setSelectedJob(jobName);
    } catch {
      // silently ignore
    }
  }, [startDate, endDate]);

  // Initial load + auto-refresh every 30s
  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleJobClick = (jobName) => {
    setJobFilter(jobName);
    fetchTimeseries(jobName);
  };

  // When job filter changes via Select dropdown, auto-fetch timeseries too
  const handleJobFilterChange = (value) => {
    setJobFilter(value);
    if (value) {
      fetchTimeseries(value);
    } else {
      setTimeseries(null);
      setSelectedJob(null);
    }
  };

  // Job filter options for Select
  const jobOptions = [
    { label: 'All jobs', value: '' },
    ...jobs.map(j => ({ label: j.job_name, value: j.job_name })),
  ];

  // Area chart data
  const areaChartData = timeseries
    ? [{
        name: timeseries.job_name,
        data: timeseries.series.map(pt => ({ key: pt.key, value: pt.value })),
      }]
    : null;

  // DataTable rows
  const tableRows = runs.map(run => [
    run.job_name,
    <StatusBadge key={run.run_id + '_s'} status={run.status} />,
    formatDateTime(run.started_at),
    formatDuration(run.duration_seconds),
    run.exit_code ?? '—',
    run.cpu_peak_percent != null ? `${run.cpu_peak_percent.toFixed(1)}%` : '—',
    run.mem_peak_mb != null ? `${run.mem_peak_mb.toFixed(1)} MB` : '—',
    <Button key={run.run_id + '_b'} size="slim" onClick={() => setDetailRun(run)}>Details</Button>,
  ]);

  return (
    <PolarisVizProvider>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center">
          <BlockStack gap="0">
            <Text variant="headingLg" as="h2">Cron Monitor</Text>
            <Text variant="bodySm" tone="subdued">Monitor scheduled job runs, timing, CPU &amp; memory usage</Text>
          </BlockStack>
          <Button onClick={fetchData} disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
        </InlineStack>
        <Layout>
          {/* Filters */}
          <Layout.Section>
            <Card>
              <Box padding="400">
                <BlockStack gap="400">
                  <Text variant="headingMd" as="h2">Date Range &amp; Filter</Text>
                  <InlineStack gap="400" wrap blockAlign="end">
                    <DatePickerPopover label="Start Date" selected={startDate} onSelect={(d) => { setStartDate(d); setActivePreset(null); }} />
                    <DatePickerPopover label="End Date"   selected={endDate}   onSelect={(d) => { setEndDate(d); setActivePreset(null); }} />
                    <div style={{ minWidth: 200 }}>
                      <Select
                        label="Job"
                        labelInline
                        options={jobOptions}
                        value={jobFilter}
                        onChange={handleJobFilterChange}
                      />
                    </div>
                    <Button variant="primary" onClick={fetchData} disabled={loading} size="large">
                      Apply
                    </Button>
                  </InlineStack>
                  <InlineStack gap="200" blockAlign="center">
                    <Text variant="bodySm" tone="subdued">Zoom:</Text>
                    {[
                      { label: '1D', days: 1 },
                      { label: '3D', days: 3 },
                      { label: '7D', days: 7 },
                      { label: '14D', days: 14 },
                      { label: '30D', days: 30 },
                    ].map(preset => (
                      <Button
                        key={preset.label}
                        size="slim"
                        variant={activePreset === preset.days ? 'primary' : 'secondary'}
                        onClick={() => applyPreset(preset.days)}
                      >
                        {preset.label}
                      </Button>
                    ))}
                  </InlineStack>
                </BlockStack>
              </Box>
            </Card>
          </Layout.Section>

          {/* Error */}
          {error && (
            <Layout.Section>
              <Banner tone="critical" onDismiss={() => setError('')}>{error}</Banner>
            </Layout.Section>
          )}

          {/* Stats row */}
          {stats && (
            <Layout.Section>
              <InlineStack gap="400" wrap>
                <div style={{ flex: 1, minWidth: 160 }}>
                  <Card>
                    <Box padding="400">
                      <BlockStack gap="100">
                        <Text variant="headingLg" fontWeight="bold">{stats.total ?? 0}</Text>
                        <Text variant="bodySm" tone="subdued">Total Runs</Text>
                      </BlockStack>
                    </Box>
                  </Card>
                </div>
                <div style={{ flex: 1, minWidth: 160 }}>
                  <Card>
                    <Box padding="400">
                      <BlockStack gap="100">
                        <Text variant="headingLg" fontWeight="bold" tone="success">{stats.successes ?? 0}</Text>
                        <Text variant="bodySm" tone="subdued">Successful</Text>
                      </BlockStack>
                    </Box>
                  </Card>
                </div>
                <div style={{ flex: 1, minWidth: 160 }}>
                  <Card>
                    <Box padding="400">
                      <BlockStack gap="100">
                        <Text variant="headingLg" fontWeight="bold" tone="critical">{stats.failures ?? 0}</Text>
                        <Text variant="bodySm" tone="subdued">Failed</Text>
                      </BlockStack>
                    </Box>
                  </Card>
                </div>
                <div style={{ flex: 1, minWidth: 160 }}>
                  <Card>
                    <Box padding="400">
                      <BlockStack gap="100">
                        <Text variant="headingLg" fontWeight="bold">{stats.running ?? 0}</Text>
                        <Text variant="bodySm" tone="subdued">Currently Running</Text>
                      </BlockStack>
                    </Box>
                  </Card>
                </div>
                <div style={{ flex: 1, minWidth: 160 }}>
                  <Card>
                    <Box padding="400">
                      <BlockStack gap="100">
                        <Text variant="headingLg" fontWeight="bold">
                          {stats.avg_duration != null ? formatDuration(stats.avg_duration) : '—'}
                        </Text>
                        <Text variant="bodySm" tone="subdued">Avg Duration</Text>
                      </BlockStack>
                    </Box>
                  </Card>
                </div>
              </InlineStack>
            </Layout.Section>
          )}

          {/* Gantt Timeline */}
          <Layout.Section>
            <Card>
              <Box padding="400">
                <BlockStack gap="300">
                  <InlineStack align="space-between" blockAlign="center">
                    <Text variant="headingMd" as="h2">Timeline</Text>
                    <InlineStack gap="300">
                      {Object.entries(STATUS_COLORS).map(([status, color]) => (
                        <InlineStack key={status} gap="100" blockAlign="center">
                          <div style={{ width: 12, height: 12, borderRadius: 2, backgroundColor: color, flexShrink: 0 }} />
                          <Text variant="bodySm" tone="subdued">{status.charAt(0).toUpperCase() + status.slice(1)}</Text>
                        </InlineStack>
                      ))}
                    </InlineStack>
                  </InlineStack>
                  <Text variant="bodySm" tone="subdued">Click a job row or label to view its activity chart below.</Text>
                  <GanttChart
                    runs={runs}
                    startDate={startDate.toISOString().slice(0, 19)}
                    endDate={toISODate(endDate)}
                    onJobClick={handleJobClick}
                  />
                </BlockStack>
              </Box>
            </Card>
          </Layout.Section>

          {/* Area Chart for selected job timeseries */}
          {areaChartData && (
            <Layout.Section>
              <Card>
                <Box padding="400">
                  <BlockStack gap="300">
                    <InlineStack align="space-between" blockAlign="center">
                      <Text variant="headingMd" as="h2">
                        {selectedJob} — Activity (seconds running per hour)
                      </Text>
                      <Button size="slim" onClick={() => { setTimeseries(null); setSelectedJob(null); }}>
                        Clear
                      </Button>
                    </InlineStack>
                    <div style={{ height: 280 }}>
                      <StackedAreaChart
                        data={areaChartData}
                        isAnimated
                      />
                    </div>
                  </BlockStack>
                </Box>
              </Card>
            </Layout.Section>
          )}

          {/* Jobs summary table */}
          {jobs.length > 0 && (
            <Layout.Section>
              <Card>
                <Box padding="400">
                  <BlockStack gap="300">
                    <InlineStack align="space-between" blockAlign="center">
                      <Text variant="headingMd" as="h2">Jobs Summary{jobFilter ? ` — ${jobFilter}` : ''}</Text>
                      {jobFilter && (
                        <Button size="slim" onClick={() => handleJobFilterChange('')}>Clear filter</Button>
                      )}
                    </InlineStack>
                    <DataTable
                      columnContentTypes={['text', 'numeric', 'numeric', 'numeric', 'numeric', 'text', 'text']}
                      headings={['Job Name', 'Total Runs', 'Success', 'Failed', 'Running', 'Avg Duration', 'Last Run']}
                      rows={(jobFilter ? jobs.filter(j => j.job_name === jobFilter) : jobs).map(j => [
                        <Button
                          key={j.job_name}
                          plain
                          onClick={() => handleJobClick(j.job_name)}
                        >
                          {j.job_name}
                        </Button>,
                        j.total_runs,
                        j.success_count,
                        j.failed_count,
                        j.running_count,
                        formatDuration(j.avg_duration),
                        formatDateTime(j.last_run),
                      ])}
                    />
                  </BlockStack>
                </Box>
              </Card>
            </Layout.Section>
          )}

          {/* Runs DataTable */}
          <Layout.Section>
            <Card>
              <Box padding="400">
                <BlockStack gap="300">
                  <Text variant="headingMd" as="h2">
                    Runs ({runs.length})
                  </Text>
                  {runs.length === 0 ? (
                    <Text tone="subdued">No runs found for the selected date range and filters.</Text>
                  ) : (
                    <DataTable
                      columnContentTypes={['text', 'text', 'text', 'text', 'numeric', 'text', 'text', 'text']}
                      headings={['Job Name', 'Status', 'Started', 'Duration', 'Exit Code', 'CPU Peak', 'Mem Peak', 'Actions']}
                      rows={tableRows}
                    />
                  )}
                </BlockStack>
              </Box>
            </Card>
          </Layout.Section>
        </Layout>

        {/* Run detail modal */}
        <RunDetailModal run={detailRun} onClose={() => setDetailRun(null)} />
      </BlockStack>
    </PolarisVizProvider>
  );
}

export default CronMonitorTab;
